import type {
  AudienceData,
  InterestsAudienceData,
  MarketingCustomAudience,
} from "../interfaces/KYDMarketing";
import {
  formatInterestsAudienceName,
  hydrateCustomAudiencesWithInterests,
  isInterestsAudience,
} from "./interestAudiences";
import { normalizeStoredCustomAudiences } from "./storedCustomAudienceRow";

function mapInterestsMarketingAudience(
  audience: MarketingCustomAudience
): InterestsAudienceData {
  const interests = (audience.metadata?.interests ?? []).map((i) => ({
    key: String(i.key ?? ""),
    value: String(i.value ?? i.key ?? ""),
  }));
  return {
    id: audience.id,
    external_id: audience.external_id,
    name: audience.name ?? formatInterestsAudienceName(interests),
    audience_type: "interests",
    interests,
    created_at:
      audience.metadata?.updated_time ?? audience.metadata?.created_time,
  };
}

/** Drop empty shells the mapper used to surface as default-type KYD rows. */
export function isMaterialWireCustomAudience(audience: AudienceData): boolean {
  if (isInterestsAudience(audience)) {
    return audience.interests.length > 0;
  }
  switch (audience.audience_type) {
    case "kyd":
      return (
        Boolean((audience.name ?? "").trim()) ||
        (audience.genres?.length ?? 0) > 0 ||
        (audience.artists?.length ?? 0) > 0 ||
        (audience.event_ids?.length ?? 0) > 0 ||
        Boolean((audience.segment_id ?? "").trim()) ||
        Boolean(audience.lookalike?.ratio)
      );
    case "meta":
      return Boolean(String(audience.external_id ?? audience.id ?? "").trim());
    case "csv":
      return Boolean(
        (audience.file_key ?? "").trim() || (audience.name ?? "").trim()
      );
    case "page_engagers":
    case "page_lookalike":
      return Boolean(
        (audience.page_id ?? "").trim() || (audience.name ?? "").trim()
      );
    default:
      return false;
  }
}

/**
 * Maps persisted rows, drops immaterial shells, and synthesizes a interests bundle
 * from flat `targeting.interests` when needed (guided-edit GET).
 */
export function prepareGuidedEditCustomAudiencesForGet(params: {
  custom_audiences: readonly MarketingCustomAudience[];
  flatInterests?: readonly { key: string; value: string }[] | null;
}): AudienceData[] {
  const custom_audiences =
    normalizeStoredCustomAudiences(params.custom_audiences) ??
    params.custom_audiences;
  const mapped = custom_audiences.map(mapMarketingCustomAudienceToAudienceData);
  return hydrateCustomAudiencesWithInterests({
    custom_audiences: mapped.filter(isMaterialWireCustomAudience),
    flatInterests: params.flatInterests,
  });
}

/** Human-readable label for one wire audience (detail cards, summaries). */
export function wireAudienceDisplayLabel(audience: AudienceData): string | null {
  if (isInterestsAudience(audience)) {
    const name =
      (audience.name ?? "").trim() ||
      formatInterestsAudienceName(audience.interests);
    return name || null;
  }
  const name = (audience.name ?? "").trim();
  if (name) return name;
  if (
    audience.audience_type === "kyd" &&
    typeof audience.description === "string" &&
    audience.description.trim()
  ) {
    return audience.description.trim();
  }
  if (audience.audience_type === "meta") {
    const id = String(audience.external_id ?? audience.id ?? "").trim();
    return id || null;
  }
  return null;
}

/** Comma-separated audience names for ad set detail (includes hydrated Meta interests). */
export function formatAdsetTargetingAudienceSummary(params: {
  custom_audiences?: readonly MarketingCustomAudience[];
  flatInterests?: readonly { key: string; value: string }[] | null;
}): string | null {
  const wire = prepareGuidedEditCustomAudiencesForGet({
    custom_audiences: params.custom_audiences ?? [],
    flatInterests: params.flatInterests,
  });
  const names = wire
    .map(wireAudienceDisplayLabel)
    .filter((n): n is string => Boolean(n));
  return names.length > 0 ? names.join(", ") : null;
}

/**
 * Maps persisted {@link MarketingCustomAudience} to guided-edit / launch wire
 * {@link AudienceData}. Lives in common so the dashboard API payload builder stays
 * thin and does not duplicate audience typing in a large “global” service.
 */
export function mapMarketingCustomAudienceToAudienceData(
  audience: MarketingCustomAudience
): AudienceData {
  const metaInterests = audience.metadata?.interests ?? [];
  if (audience.type === "interests" || metaInterests.length > 0) {
    return mapInterestsMarketingAudience(audience);
  }

  const base = {
    id: audience.id,
    external_id: audience.external_id,
    name: audience.name,
  };
  switch (audience.type) {
    case "kyd":
      return {
        ...base,
        audience_type: "kyd",
        description: audience.metadata?.description,
        created_at: audience.metadata?.updated_time,
        segment_id: audience.metadata?.segment_id,
        genres: audience.metadata?.genres ?? [],
        artists: audience.metadata?.artists ?? [],
        event_ids: audience.metadata?.events,
        lookalike: audience.metadata?.lookalike_ratio
          ? {
              name: audience.metadata.lookalike_name ?? audience.name ?? "",
              ratio: audience.metadata.lookalike_ratio,
            }
          : undefined,
      };
    case "csv":
      return {
        ...base,
        audience_type: "csv",
        description: audience.metadata?.description ?? "",
        file_key: audience.metadata?.file_key ?? "",
        mapping: audience.metadata?.mapping ?? [],
        schema_fields: audience.metadata?.schema_fields ?? [],
        created_at: audience.metadata?.updated_time,
      };
    case "page_lookalike":
      return {
        ...base,
        audience_type: "page_lookalike",
        name: audience.name ?? "",
        description: audience.metadata?.description ?? "",
        page_type: audience.metadata?.page_type,
        page_id: audience.metadata?.page_id ?? "",
        page_name: audience.metadata?.page_name ?? "",
        lookalike: audience.metadata?.lookalike_ratio
          ? {
              name: audience.metadata.lookalike_name ?? "",
              ratio: audience.metadata.lookalike_ratio,
            }
          : undefined,
        created_at: audience.metadata?.updated_time,
      };
    case "page_engagers":
      return {
        ...base,
        audience_type: "page_engagers",
        name: audience.name ?? "",
        description: audience.metadata?.description ?? "",
        page_type: audience.metadata?.page_type,
        page_id: audience.metadata?.page_id ?? "",
        page_name: audience.metadata?.page_name ?? "",
        created_at: audience.metadata?.updated_time,
      };
    case "meta":
      return {
        ...base,
        audience_type: "meta",
        description: audience.metadata?.description ?? "",
        subtype: audience.metadata?.subtype ?? "",
        time_created: audience.metadata?.created_time,
        time_updated: audience.metadata?.updated_time,
        created_at: audience.metadata?.updated_time,
      };
    default:
      return {
        ...base,
        audience_type: "kyd",
        genres: [],
        artists: [],
      };
  }
}
